import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { getScheduleTypesDropdown } from "../../api/scheduleTypeApi";
import { getScheduleStatusesDropdown } from "../../api/scheduleStatusApi";
import { getPriorityTypesDropdown } from "../../api/priorityTypeApi";
import { filterActiveMasterRows } from "../../api/apiHelpers";

const ScheduleContext = createContext(null);

export function ScheduleProvider({ children }) {
  const [scheduleTypes, setScheduleTypes] = useState([]);
  const [scheduleStatuses, setScheduleStatuses] = useState([]);
  const [priorityTypes, setPriorityTypes] = useState([]);
  const [loadingLookups, setLoadingLookups] = useState(false);
  const [lookupError, setLookupError] = useState("");

  const loadLookups = useCallback(async () => {
    setLoadingLookups(true);
    setLookupError("");
    try {
      const [typeResp, statusResp, priorityResp] = await Promise.all([
        getScheduleTypesDropdown(),
        getScheduleStatusesDropdown(),
        getPriorityTypesDropdown(),
      ]);
      setScheduleTypes(filterActiveMasterRows(typeResp?.data ?? []));
      setScheduleStatuses(filterActiveMasterRows(statusResp?.data ?? []));
      setPriorityTypes(filterActiveMasterRows(priorityResp?.data ?? []));
    } catch (err) {
      setLookupError(err?.message || "Failed to load schedule lookups");
      setScheduleTypes([]);
      setScheduleStatuses([]);
      setPriorityTypes([]);
    } finally {
      setLoadingLookups(false);
    }
  }, []);

  const value = useMemo(
    () => ({
      scheduleTypes,
      scheduleStatuses,
      priorityTypes,
      loadingLookups,
      lookupError,
      loadLookups,
    }),
    [scheduleTypes, scheduleStatuses, priorityTypes, loadingLookups, lookupError, loadLookups],
  );

  return <ScheduleContext.Provider value={value}>{children}</ScheduleContext.Provider>;
}

export function useScheduleContext() {
  const ctx = useContext(ScheduleContext);
  if (!ctx) {
    throw new Error("useScheduleContext must be used within ScheduleProvider");
  }
  return ctx;
}
